import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

const ExhibitorListingItem = ({
  exhibitor,
  onSelect,
  isSelected,
  aggregated = true,
}) => {
  // Safety check - return null if exhibitor is not provided
  if (!exhibitor) {
    return null;
  }

  const handleClick = () => {
    onSelect(exhibitor);
    // Analytics tracking
    if (typeof gtag === 'function') {
      gtag('event', 'exhibitor_profile_view', {
        exhibitor_name: exhibitor.name,
        exhibitor_id: exhibitor.id,
      });
    }
  };

  const getExhibitorTypeIcon = type => {
    // Map the React component types to backend exhibitor_type keys
    const typeMapping = {
      Artists: 'artist',
      Authors: 'author',
      'Community Groups': 'community_group',
      Entertainers: 'entertainer',
      'Food & Drink': 'food_drink_vendor',
      Makers: 'maker',
      Sponsors: 'sponsor',
      Exhibitors: 'exhibitor',
    };

    // Get the backend type key
    const backendType = typeMapping[type] || 'exhibitor';

    // Use the exact icons from Ruby EXHIBITOR_TYPE_ICONS
    switch (backendType) {
      case 'artist':
        return 'fa-paint-brush';
      case 'author':
        return 'fa-book';
      case 'community_group':
        return 'fa-group';
      case 'entertainer':
        return 'fa-music';
      case 'food_drink_vendor':
        return 'fa-cutlery';
      case 'maker':
        return 'fa-gavel';
      case 'sponsor':
        return 'fa-star';
      case 'exhibitor':
      default:
        return 'fa-shopping-cart';
    }
  };

  const formatLocation = location => {
    if (!location || !location.label) return '';
    // Remove duplicate text from locations as specified in requirements
    const parts = location.label.split(',').map(part => part.trim());
    if (parts.length > 1) {
      const firstPart = parts[0];
      const baseName = firstPart.split(':')[0];
      // Remove duplicate base names from subsequent parts
      const uniqueParts = [firstPart];
      for (let i = 1; i < parts.length; i++) {
        if (!parts[i].startsWith(baseName)) {
          uniqueParts.push(parts[i]);
        } else {
          // Extract just the booth/table number
          const match = parts[i].match(/(\d+)/);
          if (match) {
            uniqueParts.push(match[1]);
          }
        }
      }
      return uniqueParts.join(', ');
    }
    return location.label;
  };

  const formatMultipleLocations = locations => {
    if (!locations || locations.length === 0) return '';

    if (locations.length === 1) {
      return formatLocation(locations[0]);
    }

    // Group locations by base name (e.g., "Exhibit Hall")
    const locationGroups = {};

    locations.forEach(location => {
      if (!location || !location.label) return;

      const parts = location.label.split('::').map(part => part.trim());
      const baseName = parts[0] || 'Location';
      const boothInfo = parts[1] || location.label;

      if (!locationGroups[baseName]) {
        locationGroups[baseName] = [];
      }
      locationGroups[baseName].push(boothInfo);
    });

    // Format grouped locations
    const formattedGroups = Object.entries(locationGroups).map(
      ([baseName, booths]) => {
        if (booths.length === 1) {
          return `${baseName} :: ${booths[0]}`;
        } else {
          // Extract booth numbers and format compactly
          const boothNumbers = booths.map(booth => {
            const match = booth.match(/Booth (\d+)/);
            return match ? match[1] : booth;
          });
          return `${baseName} :: Booths ${boothNumbers.join(', ')}`;
        }
      },
    );

    return formattedGroups.join(' | ');
  };

  const truncateTags = (tags, maxLength = 3) => {
    if (!tags || tags.length === 0) return [];
    return tags.slice(0, maxLength);
  };

  const truncateText = (text, maxLength = 80) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const renderLocationInfo = () => {
    if (aggregated && exhibitor.locations && exhibitor.locations.length > 0) {
      const locationText = formatMultipleLocations(exhibitor.locations);
      return (
        <div className="exhibitor-location" style={{ fontSize: '0.9em' }}>
          <i className="fa fa-map-marker"></i>
          <span>{truncateText(locationText)}</span>
        </div>
      );
    } else if (exhibitor.location) {
      return (
        <div className="exhibitor-location" style={{ fontSize: '0.9em' }}>
          <i className="fa fa-map-marker"></i>
          <span>{truncateText(formatLocation(exhibitor.location))}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className={classNames('exhibitor-listing-item', {
        selected: isSelected,
        'multi-location':
          aggregated && exhibitor.locations && exhibitor.locations.length > 1,
      })}
      onClick={handleClick}
      style={{ padding: '8px 12px' }}
    >
      <div className="exhibitor-header" style={{ marginBottom: '4px' }}>
        <div className="exhibitor-type-icon">
          {exhibitor.isSponsor ? (
            <i className="fa fa-star sponsor-star" title="Sponsor"></i>
          ) : (
            <i
              className={`fa ${getExhibitorTypeIcon(exhibitor.exhibitorType)}`}
            ></i>
          )}
        </div>
        <div className="exhibitor-name">{exhibitor.name}</div>
        {/* Removed badge showing number of locations */}
      </div>

      <div className="exhibitor-details" style={{ gap: '8px' }}>
        <div className="exhibitor-logo" style={{ flexShrink: 0 }}>
          {exhibitor.avatarUrl && (
            <img
              src={exhibitor.avatarUrl}
              alt={`${exhibitor.name} logo`}
              style={{backgroundColor: '#FFFFFF'}}
              onError={e => {
                e.target.style.display = 'none';
              }}
            />
          )}
        </div>

        <div className="exhibitor-info" style={{ minWidth: 0, flex: 1 }}>
          <div
            className="exhibitor-tags"
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              overflow: 'hidden',
              gap: '4px',
              marginBottom: '4px',
              alignItems: 'center',
            }}
          >
            {truncateTags(exhibitor.tags, 3).map((tag, index) => (
              <span
                key={index}
                className="tag-capsule"
                style={{
                  flexShrink: 0,
                  padding: '2px 8px',
                  fontSize: '0.85em',
                  whiteSpace: 'nowrap',
                }}
              >
                {tag}
              </span>
            ))}
            {exhibitor.tags && exhibitor.tags.length > 3 && (
              <span
                className="tag-more"
                style={{
                  flexShrink: 0,
                  fontSize: '0.85em',
                  opacity: 0.7,
                  fontStyle: 'italic',
                }}
              >
                +{exhibitor.tags.length - 3} more
              </span>
            )}
          </div>

          {renderLocationInfo()}
        </div>
      </div>
    </div>
  );
};

ExhibitorListingItem.propTypes = {
  exhibitor: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  isSelected: PropTypes.bool,
  aggregated: PropTypes.bool,
};

ExhibitorListingItem.defaultProps = {
  isSelected: false,
  aggregated: true,
};

export default ExhibitorListingItem;
